import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Zap, ArrowRight, CheckCircle2, Star, DollarSign, MapPin, Search, Phone, Shield, Clock } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.55, ease: [0.25, 0.46, 0.45, 0.94] } }),
};

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/d555aca8-0b23-4e3e-b11c-8456de8ed400/images/28c5d5d238f27fddaab88353d2bc8a486bc24486434d1081aa62ec75ea9c06f3.png";
const IMG_COUPLE = "https://images.unsplash.com/photo-1758523419540-035814b85c80?w=800&q=80";
const IMG_SOLAR = "https://images.pexels.com/photos/9875683/pexels-photo-9875683.jpeg?auto=compress&cs=tinysrgb&w=800";
const IMG_SUPPORT = "https://images.unsplash.com/photo-1766066014237-00645c74e9c6?w=800&q=80";
const IMG_SKYLINE = "https://images.pexels.com/photos/302769/pexels-photo-302769.jpeg?auto=compress&cs=tinysrgb&w=1920";
const ICON_ELEC = "https://static.prod-images.emergentagent.com/jobs/d555aca8-0b23-4e3e-b11c-8456de8ed400/images/dc6657cc51e99322af4a92fcc1ecb4ea55a1b1c5674a56d9ee758e8e5f2865e2.png";
const ICON_BIZ = "https://static.prod-images.emergentagent.com/jobs/d555aca8-0b23-4e3e-b11c-8456de8ed400/images/7cbd76b65936c8e7a3004f3f98a1349853942942ff6a311c587e6bdbe9927b27.png";
const AVATAR_1 = "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=100&h=100&fit=crop&crop=face";
const AVATAR_2 = "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=100&h=100&fit=crop&crop=face";

const TESTIMONIALS = [
  { name: "Sarah Mitchell", location: "Sydney, NSW", text: "Sales Corp saved us over $600 on our annual electricity bill. The process was incredibly easy and the team was very helpful.", rating: 5, avatar: AVATAR_1 },
  { name: "James Chen", location: "Melbourne, VIC", text: "As a small business owner, I was paying way too much for energy. Sales Corp found me a plan that cut my costs by 30%.", rating: 5, avatar: AVATAR_2 },
  { name: "Emma Thompson", location: "Brisbane, QLD", text: "We switched our electricity through Sales Corp. Saving nearly $700 a year now. Highly recommend!", rating: 5, avatar: AVATAR_1 },
  { name: "David Williams", location: "Adelaide, SA", text: "Professional, transparent, and genuinely focused on getting the best deal. Saved us thousands across our three business locations.", rating: 5, avatar: AVATAR_2 },
];

const FAQS = [
  { q: "How does the comparison process work?", a: "Simply enter your address, tell us about your current energy setup, and we'll find you a better electricity plan. We present the best options and help you switch if you choose to." },
  { q: "Is the service free?", a: "Yes, 100% free. We're paid by providers when you switch through us, so there's never a cost to you." },
  { q: "How long does it take?", a: "The initial comparison form takes under 2 minutes. Our team will contact you within 24 hours with personalised comparison results." },
  { q: "Can you help businesses?", a: "Absolutely. We work with businesses of all sizes across Australia, from small offices to large commercial operations." },
];

function useCountUp(target, duration = 2000) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        const start = Date.now();
        const tick = () => {
          const elapsed = Date.now() - start;
          const progress = Math.min(elapsed / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          setCount(Math.floor(eased * target));
          if (progress < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }
    }, { threshold: 0.3 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target, duration]);
  return [count, ref];
}

function useGooglePlaces(inputRef, onSelect) {
  const callbackRef = useRef(onSelect);
  callbackRef.current = onSelect;
  useEffect(() => {
    const apiKey = process.env.REACT_APP_GOOGLE_PLACES_API_KEY;
    if (!apiKey || !inputRef.current) return;
    const initAC = () => {
      if (!window.google?.maps?.places) return;
      const ac = new window.google.maps.places.Autocomplete(inputRef.current, {
        componentRestrictions: { country: "au" },
        types: ["address"],
      });
      ac.addListener("place_changed", () => {
        const place = ac.getPlace();
        if (place?.formatted_address) callbackRef.current(place.formatted_address);
      });
    };
    if (window.google?.maps?.places) { initAC(); return; }
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.onload = initAC;
    document.head.appendChild(script);
  }, [inputRef]);
}

/* ─── HERO ─── */
function HeroSection() {
  const navigate = useNavigate();
  const [address, setAddress] = useState("");
  const inputRef = useRef(null);
  useGooglePlaces(inputRef, (addr) => setAddress(addr));

  const handleCompare = () => {
    if (address) navigate(`/compare?address=${encodeURIComponent(address)}`);
  };

  return (
    <section className="relative min-h-[92vh] flex items-center overflow-hidden bg-[#0B203B]" data-testid="hero-section">
      <div className="absolute inset-0">
        <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-30" />
      </div>
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-20 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <motion.p className="text-sm font-bold uppercase tracking-[0.2em] text-[#FF6600] mb-6" initial="hidden" animate="visible" variants={fadeUp}>
              Saving Money on Your Electricity
            </motion.p>
            <motion.h1
              className="text-5xl md:text-6xl font-black text-white leading-none tracking-tighter mb-6"
              initial="hidden" animate="visible" variants={fadeUp} custom={1}
            >
              Find a Better <br/><span className="text-[#FF6600]">Energy</span> Deal Today
            </motion.h1>
            <motion.p
              className="text-lg text-gray-300 mb-10 max-w-lg leading-relaxed"
              initial="hidden" animate="visible" variants={fadeUp} custom={2}
            >
              Compare electricity plans from Australia's top providers. Free, fast, and zero obligation.
            </motion.p>

            {/* Search bar — glass card */}
            <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={3} data-testid="hero-comparison-form">
              <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-2xl p-2 max-w-xl shadow-2xl">
                <div className="bg-white rounded-xl flex items-center gap-2 pr-2">
                  <MapPin className="text-[#FF6600] w-5 h-5 ml-4 flex-shrink-0" />
                  <input
                    ref={inputRef}
                    type="text"
                    placeholder="Enter your address"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && address && handleCompare()}
                    className="flex-1 text-base py-4 border-none outline-none text-gray-900 placeholder:text-gray-400 bg-transparent font-medium min-w-0"
                    data-testid="hero-address-input"
                  />
                  <Button
                    onClick={handleCompare}
                    disabled={!address}
                    className="bg-[#FF6600] hover:bg-[#E55C00] text-white font-bold px-6 py-3 rounded-xl text-sm shadow-lg hover:shadow-xl transition-all active:scale-95 disabled:opacity-40 flex-shrink-0"
                    data-testid="hero-compare-btn"
                  >
                    <Search className="w-4 h-4 mr-2" /> Compare
                  </Button>
                </div>
              </div>
            </motion.div>

            {/* Trust row */}
            <motion.div className="flex flex-wrap gap-8 mt-10" initial="hidden" animate="visible" variants={fadeUp} custom={4}>
              {["100% Free Service", "Takes Under 2 Minutes", "No Obligation to Switch"].map((t, i) => (
                <span key={i} className="flex items-center gap-2 text-sm text-gray-400 font-medium">
                  <CheckCircle2 className="w-4 h-4 text-[#10B981]" /> {t}
                </span>
              ))}
            </motion.div>
          </div>

          {/* Right: Image + floating cards */}
          <motion.div className="hidden lg:block" initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5, duration: 0.8 }}>
            <div className="relative">
              <img src={IMG_COUPLE} alt="Happy couple saving on energy" className="rounded-3xl shadow-2xl shadow-black/40 w-full h-[440px] object-cover" />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] p-5 border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-[#10B981]/10 flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-[#10B981]" />
                  </div>
                  <div>
                    <p className="text-2xl font-black text-gray-900 tracking-tight">$4.5M+</p>
                    <p className="text-sm text-gray-500">Savings delivered</p>
                  </div>
                </div>
              </div>
              <div className="absolute -top-5 -right-5 bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] p-4 border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="flex -space-x-2">
                    <img src={AVATAR_1} alt="" className="w-8 h-8 rounded-full border-2 border-white object-cover" />
                    <img src={AVATAR_2} alt="" className="w-8 h-8 rounded-full border-2 border-white object-cover" />
                    <div className="w-8 h-8 rounded-full bg-[#FF6600] border-2 border-white flex items-center justify-center text-white text-xs font-bold">+</div>
                  </div>
                  <div>
                    <p className="text-sm font-black text-gray-900">15,000+</p>
                    <p className="text-xs text-gray-400">Customers helped</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ─── SERVICES ─── */
function ServicesOverview() {
  return (
    <section className="py-24 md:py-32 bg-[#F9FAFB]" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div className="text-center mb-16" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#FF6600] mb-3">Our Services</p>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight">What Can We Help You With?</h2>
          <p className="text-base text-gray-500 mt-4 max-w-xl mx-auto">Choose your service and we'll find you a better deal.</p>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 max-w-4xl mx-auto">
          {[
            { title: "Electricity", desc: "We'll find you a better electricity plan that saves you real money on your bills.", savings: "Up to $580/yr", link: "electricity", icon: ICON_ELEC, image: IMG_SOLAR },
            { title: "Business Electricity", desc: "Specialised electricity plans for businesses of all sizes. One comparison, real savings.", savings: "Up to $2,400/yr", link: "business-energy", icon: ICON_BIZ, image: IMG_SKYLINE },
          ].map((s, i) => (
            <motion.div key={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i}>
              <Link
                to={`/compare?service=${s.link}`}
                className="block bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)] group hover:-translate-y-2 transition-all duration-300"
                data-testid={`service-card-${s.link}`}
              >
                <div className="relative h-52 overflow-hidden">
                  <img src={s.image} alt={s.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                  <div className="absolute bottom-4 left-4">
                    <img src={s.icon} alt="" className="w-14 h-14 drop-shadow-lg" />
                  </div>
                </div>
                <div className="p-8">
                  <h3 className="font-bold text-gray-900 text-xl tracking-tight mb-2">{s.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed mb-6">{s.desc}</p>
                  <div className="flex items-center justify-between">
                    <span className="inline-flex items-center px-3 py-1.5 rounded-full bg-[#10B981]/10 text-[#10B981] text-sm font-bold">Save {s.savings}</span>
                    <div className="w-10 h-10 rounded-full bg-[#FF6600]/10 flex items-center justify-center group-hover:bg-[#FF6600] transition-colors duration-300">
                      <ArrowRight className="w-5 h-5 text-[#FF6600] group-hover:text-white transition-colors duration-300" />
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── HOW IT WORKS ─── */
function HowItWorks() {
  const steps = [
    { num: "01", title: "Enter Your Address", desc: "Tell us where you are and we'll find plans available in your area.", icon: MapPin },
    { num: "02", title: "We Find You a Plan", desc: "Our team analyses the market to find you an electricity plan that saves you money.", icon: Search },
    { num: "03", title: "You Save Money", desc: "Choose the plan that suits you best. We handle the switch for you.", icon: DollarSign },
  ];
  return (
    <section className="py-24 md:py-32 bg-white" data-testid="how-it-works-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#FF6600] mb-3">How It Works</p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight mb-12">Three Simple Steps to Lower Bills</h2>
            <div className="space-y-10">
              {steps.map((step, i) => (
                <motion.div key={i} className="flex gap-6 items-start group" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i}>
                  <div className="relative flex-shrink-0">
                    <span className="absolute -top-3 -left-3 text-8xl font-black text-gray-100 select-none leading-none">{step.num}</span>
                    <div className="relative w-14 h-14 rounded-2xl bg-[#FF6600] flex items-center justify-center shadow-lg shadow-[#FF6600]/20 group-hover:scale-110 transition-transform duration-300">
                      <step.icon className="w-6 h-6 text-white" strokeWidth={2} />
                    </div>
                  </div>
                  <div className="pt-1">
                    <h3 className="font-bold text-gray-900 text-lg tracking-tight mb-1">{step.title}</h3>
                    <p className="text-gray-500 text-sm leading-relaxed">{step.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
            <Link to="/compare" className="inline-block mt-10">
              <Button className="bg-[#FF6600] hover:bg-[#E55C00] text-white font-bold rounded-full px-8 py-4 text-base shadow-lg shadow-[#FF6600]/20 active:scale-95 transition-all" data-testid="how-it-works-compare-btn">
                Start Comparing <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </motion.div>
          <motion.div className="relative" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={2}>
            <img src={IMG_SUPPORT} alt="Our support team" className="rounded-3xl shadow-xl w-full h-[480px] object-cover" />
            <div className="absolute -bottom-6 left-6 right-6 backdrop-blur-xl bg-white/80 rounded-2xl shadow-xl p-6 border border-white/50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-[#FF6600]" />
                  <div>
                    <p className="text-sm font-bold text-gray-900">Need help? Call us</p>
                    <a href="tel:1300796797" className="text-lg font-black text-[#FF6600]">1300 796 797</a>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-400">Response time</p>
                  <p className="text-sm font-bold text-gray-900">Within 24 hours</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ─── WHY CHOOSE US ─── */
function WhyChooseUs() {
  return (
    <section className="py-24 md:py-32 bg-[#F9FAFB]" data-testid="why-choose-us-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div className="relative order-2 lg:order-1" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <div className="grid grid-cols-2 gap-4">
              <img src={IMG_SOLAR} alt="Solar homes" className="rounded-2xl h-56 w-full object-cover shadow-lg" />
              <img src={IMG_COUPLE} alt="Happy customers" className="rounded-2xl h-56 w-full object-cover mt-8 shadow-lg" />
            </div>
            <div className="mt-4 grid grid-cols-2 gap-4">
              <div className="bg-[#0B203B] rounded-2xl p-6 flex flex-col justify-center">
                <p className="text-4xl font-black text-[#FF6600] tracking-tighter">98%</p>
                <p className="text-sm text-white/60 mt-1">Customer Satisfaction</p>
              </div>
              <div className="bg-[#FF6600] rounded-2xl p-6 flex flex-col justify-center">
                <p className="text-4xl font-black text-white tracking-tighter">24/7</p>
                <p className="text-sm text-white/80 mt-1">Support Available</p>
              </div>
            </div>
          </motion.div>
          <motion.div className="order-1 lg:order-2" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={1}>
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#FF6600] mb-3">Why Choose Us</p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight mb-8">Why Australians Trust Sales Corp</h2>
            <div className="space-y-5">
              {[
                { icon: DollarSign, title: "Completely Free", desc: "Our service costs you nothing. We're paid by providers, not by you." },
                { icon: MapPin, title: "Australian Energy Experts", desc: "Sydney-based team with deep understanding of the local energy market." },
                { icon: Shield, title: "No Obligation", desc: "Compare freely with zero pressure to switch. The choice is always yours." },
                { icon: Clock, title: "Fast & Easy", desc: "Get results in under 2 minutes. We do the hard work." },
                { icon: Phone, title: "Ongoing Support", desc: "We're here to help even after you switch." },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4 group">
                  <div className="w-10 h-10 rounded-xl bg-[#10B981]/10 flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:bg-[#10B981]/20 transition-colors">
                    <item.icon className="w-5 h-5 text-[#10B981]" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 text-base">{item.title}</h4>
                    <p className="text-gray-500 text-sm">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ─── STATS ─── */
function StatsSection() {
  const [c1, r1] = useCountUp(15000, 2000);
  const [c2, r2] = useCountUp(4500000, 2000);
  const [c3, r3] = useCountUp(2, 1500);
  const [c4, r4] = useCountUp(98, 1500);
  return (
    <section className="relative py-20 md:py-28 overflow-hidden bg-[#0B203B]" data-testid="stats-section">
      <div className="absolute inset-0">
        <img src={IMG_SKYLINE} alt="" className="w-full h-full object-cover opacity-15" />
      </div>
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-10 text-center">
          <div ref={r1}>
            <div className="text-5xl md:text-7xl font-black text-white tracking-tighter">{c1.toLocaleString()}+</div>
            <p className="text-white/40 mt-3 text-sm font-medium">Customers Helped</p>
          </div>
          <div ref={r2}>
            <div className="text-5xl md:text-7xl font-black text-[#10B981] tracking-tighter">${(c2 / 1000000).toFixed(1)}M+</div>
            <p className="text-white/40 mt-3 text-sm font-medium">Total Savings</p>
          </div>
          <div ref={r3}>
            <div className="text-5xl md:text-7xl font-black text-[#FF6600] tracking-tighter">&lt;{c3} min</div>
            <p className="text-white/40 mt-3 text-sm font-medium">To Get a Quote</p>
          </div>
          <div ref={r4}>
            <div className="text-5xl md:text-7xl font-black text-white tracking-tighter">{c4}%</div>
            <p className="text-white/40 mt-3 text-sm font-medium">Customer Satisfaction</p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── TESTIMONIALS ─── */
function TestimonialsSection() {
  return (
    <section className="py-24 md:py-32 bg-white" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div className="text-center mb-16" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#FF6600] mb-3">Testimonials</p>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight">What Our Customers Say</h2>
          <p className="text-base text-gray-500 mt-4">Real stories from Australians who switched and saved</p>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {TESTIMONIALS.map((t, i) => (
            <motion.div
              key={i}
              className="bg-white border border-gray-100 rounded-2xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-1 transition-all duration-300"
              initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i}
            >
              <div className="flex gap-0.5 mb-5">
                {Array.from({ length: t.rating }).map((_, j) => (
                  <Star key={j} className="w-4 h-4 fill-[#FF6600] text-[#FF6600]" />
                ))}
              </div>
              <p className="text-gray-600 text-sm leading-relaxed mb-6">"{t.text}"</p>
              <div className="flex items-center gap-3">
                <img src={t.avatar} alt={t.name} className="w-10 h-10 rounded-full object-cover border-2 border-gray-100" />
                <div>
                  <p className="font-bold text-gray-900 text-sm">{t.name}</p>
                  <p className="text-xs text-gray-400">{t.location}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── FAQ ─── */
function FAQSection() {
  return (
    <section className="py-24 md:py-32 bg-[#F9FAFB]" data-testid="faq-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#FF6600] mb-3">FAQ</p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight mb-6">Frequently Asked Questions</h2>
            <p className="text-gray-500 text-base leading-relaxed mb-8">Have questions? We've got answers. If you can't find what you're looking for, don't hesitate to give us a call.</p>
            <div className="flex items-center gap-4 p-5 bg-white rounded-2xl border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <img src={IMG_SUPPORT} alt="Our support team" className="w-16 h-16 rounded-xl object-cover" />
              <div>
                <p className="text-sm font-bold text-gray-900">Still have questions?</p>
                <a href="tel:1300796797" className="text-sm font-bold text-[#FF6600]">Call 1300 796 797</a>
              </div>
            </div>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={1}>
            <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
              {FAQS.map((faq, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="bg-white border border-gray-100 rounded-xl px-6 overflow-hidden shadow-[0_4px_15px_rgb(0,0,0,0.02)]">
                  <AccordionTrigger className="text-left font-bold text-gray-900 hover:text-[#FF6600] py-5 text-sm md:text-base" data-testid={`faq-trigger-${i}`}>
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-500 text-sm leading-relaxed pb-5">{faq.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
            <div className="mt-6">
              <Link to="/faq" className="text-sm font-bold text-[#FF6600] hover:underline" data-testid="view-all-faqs-link">
                View All FAQs <ArrowRight className="w-4 h-4 inline ml-1" />
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ─── CTA ─── */
function FinalCTA() {
  return (
    <section className="relative py-24 md:py-28 overflow-hidden bg-[#2E2E33]" data-testid="final-cta-section">
      <div className="absolute inset-0">
        <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-20" style={{ transform: "scaleX(-1)" }} />
      </div>
      <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
          <h2 className="text-4xl md:text-5xl font-black text-white tracking-tighter mb-5">Ready to Start Saving?</h2>
          <p className="text-base text-white/50 mb-10 max-w-2xl mx-auto">
            Join thousands of Australians who have already found better energy deals with Sales Corp Australia. Free, fast, no obligation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/compare">
              <Button className="bg-[#FF6600] hover:bg-[#E55C00] text-white font-bold rounded-full px-10 py-5 text-base shadow-lg shadow-[#FF6600]/25 hover:shadow-xl transition-all active:scale-95" data-testid="final-cta-compare-btn">
                Compare Now <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <a href="tel:1300796797">
              <Button variant="outline" className="border-2 border-white/20 text-white font-bold rounded-full px-10 py-5 text-base hover:bg-white hover:text-gray-900 transition-all" data-testid="final-cta-call-btn">
                <Phone className="w-5 h-5 mr-2" /> 1300 796 797
              </Button>
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default function HomePage() {
  return (
    <div data-testid="home-page">
      <HeroSection />
      <ServicesOverview />
      <HowItWorks />
      <WhyChooseUs />
      <StatsSection />
      <TestimonialsSection />
      <FAQSection />
      <FinalCTA />
    </div>
  );
}
